import { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../../../components/ui/card';
import { DataTable, DataTableColumn } from '../../../components/ui/data-table';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import {
  Activity,
  Filter,
  Download,
  RefreshCcw,
  TrendingUp,
  TrendingDown,
} from 'lucide-react';
import PageHeader from '../../../components/common/PageHeader';
import TradingStatusBadge from '../../../components/trading/TradingStatusBadge';

type Trade = {
  id: string;
  strategyId: string;
  strategyName: string;
  symbol: string;
  type: 'buy' | 'sell';
  volume: number;
  openPrice: number;
  closePrice?: number;
  profit: number;
  status: 'open' | 'closed' | 'pending';
  openTime: string;
  closeTime?: string;
};

const PAMMTrades = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const trades: Trade[] = [
    {
      id: 'TRD001',
      strategyId: 'STR001',
      strategyName: 'Gold Scalper Pro',
      symbol: 'XAUUSD',
      type: 'buy',
      volume: 0.5,
      openPrice: 2025.5,
      closePrice: 2030.75,
      profit: 262.5,
      status: 'closed',
      openTime: '2024-01-20T10:30:00',
      closeTime: '2024-01-20T14:45:00',
    },
    {
      id: 'TRD002',
      strategyId: 'STR002',
      strategyName: 'Forex Trend Master',
      symbol: 'EURUSD',
      type: 'sell',
      volume: 1.0,
      openPrice: 1.085,
      closePrice: 1.082,
      profit: 300,
      status: 'closed',
      openTime: '2024-01-20T09:15:00',
      closeTime: '2024-01-20T11:30:00',
    },
    {
      id: 'TRD003',
      strategyId: 'STR003',
      strategyName: 'Conservative Growth',
      symbol: 'GBPUSD',
      type: 'buy',
      volume: 0.3,
      openPrice: 1.265,
      profit: -45,
      status: 'open',
      openTime: '2024-01-20T08:20:00',
    },
  ];

  const columns: DataTableColumn<Trade>[] = [
    {
      id: 'id',
      header: 'Trade ID',
      accessor: row => <span className="font-medium">{row.id}</span>,
      sortable: true,
      sortValue: row => row.id,
      width: '120px',
    },
    {
      id: 'strategyName',
      header: 'Strategy',
      accessor: row => row.strategyName,
      sortable: true,
      sortValue: row => row.strategyName,
      width: '180px',
    },
    {
      id: 'symbol',
      header: 'Symbol',
      accessor: row => (
        <Badge variant="outline" className="font-mono">
          {row.symbol}
        </Badge>
      ),
      sortable: true,
      sortValue: row => row.symbol,
      width: '120px',
    },
    {
      id: 'type',
      header: 'Type',
      accessor: row => (
        <span
          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
            row.type === 'buy'
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
          }`}
        >
          {row.type.toUpperCase()}
        </span>
      ),
      sortable: true,
      sortValue: row => row.type,
      width: '100px',
    },
    {
      id: 'volume',
      header: 'Volume',
      accessor: row => row.volume.toFixed(2),
      sortable: true,
      sortValue: row => row.volume,
      width: '100px',
    },
    {
      id: 'openPrice',
      header: 'Open Price',
      accessor: row => row.openPrice.toFixed(5),
      sortable: true,
      sortValue: row => row.openPrice,
      width: '120px',
    },
    {
      id: 'closePrice',
      header: 'Close Price',
      accessor: row => (row.closePrice ? row.closePrice.toFixed(5) : '-'),
      sortable: true,
      sortValue: row => row.closePrice || 0,
      width: '120px',
    },
    {
      id: 'profit',
      header: 'Profit',
      accessor: row => (
        <div className="flex items-center gap-1">
          {row.profit >= 0 ? (
            <TrendingUp className="h-4 w-4 text-green-600" />
          ) : (
            <TrendingDown className="h-4 w-4 text-red-600" />
          )}
          <span
            className={`font-semibold ${
              row.profit >= 0 ? 'text-green-600' : 'text-red-600'
            }`}
          >
            ${row.profit.toFixed(2)}
          </span>
        </div>
      ),
      sortable: true,
      sortValue: row => row.profit,
      width: '120px',
    },
    {
      id: 'status',
      header: 'Status',
      accessor: row => <TradingStatusBadge status={row.status} />,
      sortable: true,
      sortValue: row => row.status,
      width: '120px',
    },
    {
      id: 'openTime',
      header: 'Open Time',
      accessor: row => new Date(row.openTime).toLocaleString(),
      sortable: true,
      sortValue: row => row.openTime,
      width: '160px',
    },
  ];

  const filteredTrades = trades.filter(trade => {
    return (
      searchTerm === '' ||
      trade.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
      trade.strategyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      trade.id.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const totalProfit = trades.reduce((sum, trade) => sum + trade.profit, 0);
  const openTrades = trades.filter(t => t.status === 'open').length;
  const closedTrades = trades.filter(t => t.status === 'closed').length;

  return (
    <div className="space-y-6">
      <div className="sticky top-0 z-20 bg-background pt-4">
        <PageHeader
          title="Trade Monitoring"
          description="Monitor all PAMM strategy trades in real-time"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="flex items-center gap-2 p-4 bg-blue-50 rounded-lg">
          <Activity className="h-5 w-5 text-blue-600" />
          <div>
            <p className="text-sm text-muted-foreground">Total Trades</p>
            <p className="text-2xl font-bold text-blue-600">{trades.length}</p>
          </div>
        </div>

        <div className="flex items-center gap-2 p-4 bg-green-50 rounded-lg">
          <Activity className="h-5 w-5 text-green-600" />
          <div>
            <p className="text-sm text-muted-foreground">Open Trades</p>
            <p className="text-2xl font-bold text-green-600">{openTrades}</p>
          </div>
        </div>

        <div className="flex items-center gap-2 p-4 bg-purple-50 rounded-lg">
          <Activity className="h-5 w-5 text-purple-600" />
          <div>
            <p className="text-sm text-muted-foreground">Closed Trades</p>
            <p className="text-2xl font-bold text-purple-600">{closedTrades}</p>
          </div>
        </div>

        <div className="flex items-center gap-2 p-4 bg-orange-50 rounded-lg">
          <TrendingUp className="h-5 w-5 text-orange-600" />
          <div>
            <p className="text-sm text-muted-foreground">Total Profit</p>
            <p
              className={`text-2xl font-bold ${totalProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}
            >
              ${totalProfit.toFixed(2)}
            </p>
          </div>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Trade History</CardTitle>
              <CardDescription>
                Real-time monitoring of all strategy trades
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsLoading(!isLoading)}
                disabled={isLoading}
              >
                <RefreshCcw
                  className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`}
                />
                Refresh
              </Button>
              <Button variant="outline" size="sm">
                <Filter className="h-4 w-4 mr-2" />
                Filters
              </Button>
              <Button variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={columns}
            data={filteredTrades}
            searchPlaceholder="Search by symbol, strategy, or trade ID..."
            onSearchChange={setSearchTerm}
            showColumnVisibility={true}
            showExportButton={false}
            showAddButton={false}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default PAMMTrades;
